import { Channel, CommLibInstance, Connection, ConnectionInstance, ConnectionState, ContactLeg, Device, DeviceState, EventBusInstance, LegState, LoggerInstance } from "@sap/cpaas-commlib-lib";
import { App } from "../App";
import { IChatMessage, StaticTemplate, Template } from "../utils/types";
import { AppState, Configuration, Parameter } from "./configuration";
import { EcfAdapter } from "./ecfAdapter";
import { Logger } from "./logger";

function parseWssEndpoint(httpEndpoint: string): string {
	let endpoint = httpEndpoint;
	if (endpoint.indexOf("http://") !== -1) {
		endpoint = endpoint.replace("http://", "ws://");
	} else if (endpoint.indexOf("https://") !== -1) {
		endpoint = endpoint.replace("https://", "wss://");
	}
	return endpoint;
}

export abstract class CommlibService {

	static endpoint = parseWssEndpoint(Configuration.endpoint);
	static templates: { [id: string]: Template | StaticTemplate } = {};

	static setup() {
		LoggerInstance.setLevel(Configuration.logLevel);
		EventBusInstance.subscribe(Configuration.event, CommlibService.onConfigurationUpdate, undefined, Configuration.component);
	}

	static destroy() {
		EventBusInstance.unsubscribe(Configuration.event, CommlibService.onConfigurationUpdate, undefined, Configuration.component);
	}

	static async onConfigurationUpdate(channelId: string, eventId: string, data: any) {
		if (data.indexOf(Parameter.mockServer) !== -1) {
			await CommLibInstance.useMockServer(Configuration.mockServer);
		}
		if (data.indexOf(Parameter.useVideo) !== -1) {
			if (AppState.activeDevice && (AppState.activeDevice.getChannel() !== Channel.video && Configuration.useVideo || AppState.activeDevice.getChannel() === Channel.video
				&& !Configuration.useVideo)) {
				CommLibInstance.disconnect().catch(() => true);
			}
		}
		if (data.indexOf(Parameter.cad) !== -1 || data.indexOf(Parameter.userName) !== -1 || data.indexOf(Parameter.userAddress) !== -1) {
			if (AppState.activeLeg && AppState.activeLeg.getState() !== LegState.disconnected) {
				AppState.activeLeg.getContact().setCad(CommlibService.getCad()).catch(() => true);
			}
		}
		if (data.indexOf(Parameter.locale) !== -1 || !App.instance.hasTranslationProvider()) {
			try {
				App.instance.changeLanguage();
			} catch (error) {
				Logger.error("Failed to change language", error.message);
			}
		}
		if (data.indexOf(Parameter.logLevel) !== -1) {
			LoggerInstance.setLevel(Configuration.logLevel);
		}
		if (data.indexOf(Parameter.theme) !== -1) {
			document.getElementById("styles").innerHTML = Configuration.theme;
			EventBusInstance.publish(Configuration.sizeEvent, Configuration.component);
		}
		if (data.indexOf(Parameter.queues) !== -1) {
			try {
				await CommlibService.getQueueInfo();
				EventBusInstance.publish(Configuration.event, [Parameter.chatQueues], Configuration.component);
				EventBusInstance.publish(Configuration.sizeEvent, Configuration.component);
			} catch (error) {
				Logger.error("Failed to get queue information", error.message);
			}
		}
		if (data.indexOf(Parameter.endpoint) !== -1 || data.indexOf(Parameter.tenantId) !== -1) {
			// Endpoint change
			try {
				const newEndpoint = parseWssEndpoint(Configuration.endpoint);
				if (newEndpoint === CommlibService.endpoint) {
					Logger.debug("New provided endpoint is the same as old one: " + newEndpoint);
				} else {
					CommlibService.endpoint = newEndpoint;
					Logger.info("New endpoint set: " + CommlibService.endpoint);
					if (ConnectionInstance.getConnectionState() === ConnectionState.Connected) {
						await ConnectionInstance.disconnect();
					}
				}
			} catch (e) {
				// ignore
			}
		}
	}

	static connect(): Promise<Connection> {
		return CommLibInstance.connect(CommlibService.endpoint);
	}

	static disconnect(): Promise<Connection> {
		AppState.activeLeg = null;
		return CommLibInstance.disconnect();
	}

	static hasWebRTC() {
		// @ts-ignore
		return navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia || window.RTCPeerConnection;
	}

	static async registerDevice(): Promise<Device> {
		const channel = (Configuration.useVideo && this.hasWebRTC()) ? Channel.video : Channel.chat;
		let device: Device;
		for (const activeDevice of CommLibInstance.getDevices()) {
			if (activeDevice.getChannel() === channel && activeDevice.getState() !== DeviceState.disconnected) {
				device = activeDevice;
			}
		}
		if (!device) {
			const devices: any = await CommLibInstance.registerChannel([{ channel, carrier: "cctr" }]);
			device = devices[0];
		}
		if (device instanceof Error || !device) {
			throw new Error();
		}
		AppState.activeDevice = device;
		return device;
	}

	static async startChat(address: string): Promise<ContactLeg> {
		Logger.debug("Start chat on " + Channel[AppState.activeDevice.getChannel()], address);
		AppState.activeLeg = await AppState.activeDevice.newLeg({ toAddress: address });
		return AppState.activeLeg;
	}

	static async getQueueInfo() {
		const response = await (await fetch(Configuration.endpoint + "/queues", { credentials: "include" })).json();
		const queues = response.body;
		Configuration.chatQueues = [];
		for (const chatQueue of Configuration.queues) {
			for (const queue of queues) {
				if (chatQueue.address === queue.address) {
					Configuration.chatQueues.push(queue);
					break;
				}
			}
		}
	}

	static getCad() {
		let cad: any;
		if (Configuration.userAddress || Configuration.userName || Configuration.cad) {
			cad = Configuration.cad || {};
			cad.alias = Configuration.userName;
			cad.chatAddress = Configuration.userAddress;
		}
		return cad;
	}

	/**
	 * Fetch template from server
	 * @throws network and parse errors
	 */
	static async fetchTemplate(id: string): Promise<Template | StaticTemplate> {
		// TODO: allow only one request at a time to not flood RI on reload
		if (this.templates.hasOwnProperty(id)) {
			return this.templates[id];
		}
		const auth = EcfAdapter.getInstance().ecfAuthId;
		const url = Configuration.endpoint + "/RI/rci/replyTemplates/" + id + (auth ? ";jsessionid=" + auth : "");
		const res = await fetch(url, {
			method: "GET",
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json',
			},
			credentials: "include",
		});
		const template = await res.json();
		this.templates[template.id] = template;
		return template;
	}

	/**
	 * Fetch template from server
	 * @throws network and parse errors
	 */
	static async fetchStaticTemplate(name: string): Promise<Template | StaticTemplate> {
		// TODO: Decide if these should be in RI or Frontend
		if (this.templates.hasOwnProperty(name)) {
			return this.templates[name];
		}
		const res = await fetch("resources/templates/" + name + ".html", {
			method: "GET",
			headers: {
				'Accept': 'text/html',
				'Content-Type': 'text/html',
			},
			credentials: "include",
		});
		const body = await res.text();
		this.templates[name] = { body, id: name };
		return this.templates[name];
	}

	static async fetchTranscriptMessages(): Promise<IChatMessage[]> {
		const res = await fetch(Configuration.endpoint + "/contact/messages", {
			method: "GET",
			mode: "cors",
			headers: {
				'Accept': 'application/json',
				'Content-Type': 'application/json',
			},
			credentials: "include",
		});
		const json = await res.json();
		return json.messages;
	}
}
